// src/utils/authErrorHandler.ts
// Shared utility for handling authentication errors across the application

let isRefreshingAuth = false;
let refreshPromise: Promise<void> | null = null;

// Helper function to create a fetch with timeout
function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number = 10000): Promise<Response> {
  return Promise.race([
    fetch(url, options),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    ),
  ]);
}

// Helper function to retry a fetch operation with exponential backoff
async function retryFetch<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`Auth refresh failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`, error.message || error);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Attempts to refresh the user authentication session
 * Returns a promise that resolves when auth is refreshed or fails
 */
export async function refreshAuthSession(): Promise<boolean> {
  // If already refreshing, wait for that to complete
  if (isRefreshingAuth && refreshPromise) {
    try {
      await refreshPromise;
      return true;
    } catch {
      return false;
    }
  }

  // Start refresh process
  isRefreshingAuth = true;
  refreshPromise = retryFetch(async () => {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/auth/me`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      10000 // 10 second timeout
    );

    if (response.status === 401) {
      // Auth failed - session is invalid
      throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' };
    }

    if (!response.ok) {
      // Check if it's a network/server error that should be retried
      if (response.status >= 500 || response.status === 0) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      throw new Error(`Auth refresh failed: ${response.statusText}`);
    }

    // Success - session is valid
    return response.json();
  }, 3, 1000).then(() => {
    // Success
    return;
  }).catch((error) => {
    console.error('Auth refresh failed after retries:', error);
    throw error;
  }).finally(() => {
    isRefreshingAuth = false;
    refreshPromise = null;
  });

  try {
    await refreshPromise;
    return true;
  } catch {
    return false;
  }
}

/**
 * Handles a 401 authentication error by attempting to refresh the session
 * Returns true if auth was refreshed successfully, false otherwise
 */
export async function handleAuthError(retryCount = 0): Promise<boolean> {
  // Don't retry more than once
  if (retryCount > 0) {
    return false;
  }

  // Attempt to refresh auth
  const refreshed = await refreshAuthSession();
  
  if (!refreshed) {
    // If refresh failed, redirect to login page (not backend auth endpoint)
    const currentPath = window.location.pathname;
    const isLoginPage = currentPath === '/auth/callback' || 
                        currentPath.startsWith('/auth/') || 
                        currentPath === '/login' ||
                        currentPath === '/register';
    
    if (!isLoginPage) {
      // Store the intended destination
      if (currentPath !== '/main' && currentPath !== '/') {
        sessionStorage.setItem('redirectAfterLogin', currentPath + window.location.search);
      }
      // Redirect to frontend login page (not backend auth endpoint)
      window.location.href = '/login';
    }
    return false;
  }

  return true;
}

/**
 * Checks if the current request should be retried after an auth error
 */
export function shouldRetryAfterAuthError(originalRequest: any, retryCount: number): boolean {
  // Only retry once
  if (retryCount > 0) {
    return false;
  }
  
  // Don't retry if the request was already retried
  if (originalRequest._retry) {
    return false;
  }

  return true;
}

